import React, { useState } from 'react';
import { Bug, Send, AlertCircle, CheckCircle2 } from 'lucide-react';

import { useDiagnosticsBugReport } from '@/hooks/useDiagnosticsBugReport';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export interface ErrorReportButtonProps {
  /** Feature/component identifier */
  feature: string;
  /** Error object */
  error?: Error | null;
  /** Game state snapshot */
  gameState?: Record<string, unknown>;
  /** User preferences */
  preferences?: Record<string, unknown>;
  /** Additional context */
  additionalContext?: Record<string, unknown>;
  /** Display as button or link */
  variant?: 'button' | 'link';
  /** Size variant */
  size?: 'sm' | 'default';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Reusable button/link for reporting errors with diagnostic data.
 * Opens a lightweight dialog for user description and diagnostic preview.
 */
export default function ErrorReportButton({
  feature,
  error,
  gameState = {},
  preferences = {},
  additionalContext = {},
  variant = 'link',
  size = 'sm',
  className = '',
}: ErrorReportButtonProps) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const {
    submitReport,
    isSubmitting,
    isSubmitted,
    submitError,
    deliveryMethod,
    reset,
  } = useDiagnosticsBugReport({
    feature,
    error,
    gameState,
    preferences,
    additionalContext,
  });

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset state when dialog closes
      setTimeout(() => {
        reset();
        setDescription('');
        setShowPreview(false);
      }, 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitReport(description);
  };

  // Auto-close after successful submission
  React.useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        handleOpenChange(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted]);

  const triggerButton = variant === 'link' ? (
    <button
      type="button"
      className={`text-sm text-indigo-600 hover:text-indigo-700 underline inline-flex items-center gap-1 ${className}`}
    >
      <Bug className="w-3.5 h-3.5" />
      Report this issue
    </button>
  ) : (
    <Button variant="outline" size={size} className={`gap-2 ${className}`}>
      <Bug className="w-4 h-4" />
      Report Issue
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{triggerButton}</DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Report Error</DialogTitle>
          <DialogDescription>
            Help us fix this issue. Your report includes diagnostic data to help us debug the problem.
          </DialogDescription>
        </DialogHeader>

        {isSubmitted ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <p className="text-lg font-semibold text-emerald-600">Thank you!</p>
            <p className="text-sm text-slate-500 mt-2">
              {deliveryMethod === 'localStorage'
                ? 'Your report has been saved locally and will be sent when online.'
                : 'Your error report has been submitted to our team.'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {submitError && (
              <div
                role="alert"
                className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200"
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3">
                <p className="text-sm font-medium text-red-800 mb-1">Error in {feature}</p>
                <p className="text-xs text-red-700 font-mono">{error.message}</p>
              </div>
            )}

            <div>
              <Label htmlFor="description">What were you trying to do? (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what you were doing when this error occurred..."
                rows={3}
                className="mt-2"
                disabled={isSubmitting}
              />
            </div>

            <div className="rounded-md border border-slate-200 p-3 bg-slate-50 space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-slate-600">
                  <p className="font-medium mb-1">Diagnostic data will be included</p>
                  <p>
                    This includes game state, browser capabilities, display info, performance metrics,
                    and recent errors to help us debug the issue.
                  </p>
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="px-0 h-auto text-xs"
                onClick={() => setShowPreview(!showPreview)}
                disabled={isSubmitting}
              >
                {showPreview ? 'Hide preview' : 'Preview diagnostic data'}
              </Button>

              {showPreview && (
                <pre className="text-xs bg-white border border-slate-200 rounded p-2 max-h-40 overflow-auto">
                  {JSON.stringify(
                    {
                      feature,
                      route: window.location.pathname,
                      game: {
                        state: (gameState.gameState as string) || 'unknown',
                        difficulty: (preferences.difficulty as string) || 'unknown',
                        gradeLevel: (preferences.gradeLevel as string) || 'unknown',
                        roundsPlayed: (gameState.roundsPlayed as number) || 0,
                        score: (gameState.score as number) || 0,
                      },
                      ...(error && {
                        error: {
                          message: error.message,
                          name: error.name,
                        },
                      }),
                    },
                    null,
                    2
                  )}
                </pre>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              <Send className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
